import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { REPS_COLORS, SEVERITY_COLORS, OE_ELEMENTS } from '../utils/constants';
import { getSeverity, getScoreColor } from '../utils/scoring';
import { normalizeThresholds } from '../utils/bands';

const REPS_LABELS = {
  R: 'Reliability',
  E: 'Efficiency',
  P: 'Productivity',
  S: 'Safety',
};

const REPS_COLOR_MAP = {
  R: REPS_COLORS.reliability,
  E: REPS_COLORS.efficiency,
  P: REPS_COLORS.productivity,
  S: REPS_COLORS.safety,
};

/**
 * Calculate average score across R, E, P, S for an element
 */
function getElementAverage(scores) {
  if (!scores) return null;
  const values = ['R', 'E', 'P', 'S'].map((k) => scores[k] ?? 0);
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function OEBreakdown({ elementScores, thresholds }) {
  const t = normalizeThresholds(thresholds);
  const tShort = { R: t.reliability, E: t.efficiency, P: t.productivity, S: t.safety };
  const avgThreshold = (t.reliability + t.efficiency + t.productivity + t.safety) / 4;
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!elementScores) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            OE Element Breakdown
          </Typography>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No element score data available. Complete surveys to see the breakdown across
              Drift, Traps, Roadblocks, and Landmines.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        OE Element Breakdown
      </Typography>
      <Grid container spacing={2}>
        {OE_ELEMENTS.map((element) => {
          const scores = elementScores[element.key];
          const avg = getElementAverage(scores);
          const severity = avg != null ? getSeverity(avg, avgThreshold) : null;
          const severityColor = avg != null ? getScoreColor(avg, avgThreshold) : '#e0e0e0';
          const isExpanded = expanded[element.key] || false;

          return (
            <Grid item xs={12} sm={6} lg={2.4} key={element.key} /* 5 across on wide screens, 2+3 between — no orphan slot (UI review §5) */>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `4px solid ${severityColor}`,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                      {element.label}
                    </Typography>
                    {scores && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: '"Roboto Mono", monospace',
                            fontWeight: 700,
                            color: severityColor,
                          }}
                        >
                          {avg.toFixed(1)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(element.key)}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {scores ? (
                    <Collapse in={isExpanded} timeout="auto">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, mb: 0.5 }}>
                        {Object.entries(REPS_LABELS).map(([key, label]) => {
                          const score = scores[key] ?? 0;
                          const chipSeverity = getSeverity(score, tShort[key]);
                          return (
                            <Chip
                              key={key}
                              label={`${label.charAt(0)}: ${score.toFixed(1)}`}
                              size="small"
                              sx={{
                                bgcolor: `${REPS_COLOR_MAP[key]}15`,
                                color: REPS_COLOR_MAP[key],
                                fontFamily: '"Roboto Mono", monospace',
                                fontWeight: 600,
                                borderLeft: `3px solid ${getScoreColor(score, tShort[key])}`,
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Collapse>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No data
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default OEBreakdown;
